/**
 * SteelOps Plugin Registry
 *
 * Every feature in SteelOps is a plugin. Core plugins are always on.
 * Add-on plugins can be toggled per account from the Settings screen.
 *
 * Tier gates:
 *   Starter  ($149/mo) — core plugins only
 *   Shop     ($349/mo) — core + shop plugins (up to 15 employee seats)
 *   Pro      ($649/mo) — core + shop + pro plugins
 *   Enterprise ($1099/mo) — all plugins
 *
 * To add a new plugin:
 * 1. Add it here with a unique id
 * 2. Gate rendering with useFeature('your_id')
 */

export const PLUGINS = [
  // ── CORE — always enabled, all tiers ──────────────────────
  {
    id:          'job_tracking',
    name:        'Job & Piece-Mark Tracking',
    category:    'core',
    icon:        '🏗️',
    core:        true,
    tier:        'starter',
    description: 'Create jobs, add assemblies and piece marks, advance fab stages (Detail → Fit-Up → Weld → Paint → QC → Ship). The core of SteelOps.',
    route:       '/jobs',
  },
  {
    id:          'bid_builder',
    name:        'Bid Builder',
    category:    'core',
    icon:        '📋',
    core:        true,
    tier:        'starter',
    description: 'Build bids from assembly rate items, generate PDF bid packages, and send tokenized Approve/Decline links to GCs.',
    route:       '/bids',
  },
  {
    id:          'owner_dashboard',
    name:        'Owner Dashboard',
    category:    'core',
    icon:        '📊',
    core:        true,
    tier:        'starter',
    description: 'KPI cards (jobs in progress, labor hours, outstanding billings, overdue deliveries) and live stage heatmap — desktop and mobile PWA.',
    route:       '/dashboard',
  },
  {
    id:          'customers',
    name:        'Customer & GC Management',
    category:    'core',
    icon:        '🏢',
    core:        true,
    tier:        'starter',
    description: 'Manage GC and owner contacts, project addresses, and billing history.',
    route:       '/customers',
  },
  {
    id:          'team',
    name:        'Team Management',
    category:    'core',
    icon:        '👥',
    core:        true,
    tier:        'starter',
    description: 'Add employees, assign roles, and invite them to the clock-in app.',
    route:       '/team',
  },
  {
    id:          'invoicing',
    name:        'Invoicing & Payments',
    category:    'core',
    icon:        '💳',
    core:        true,
    tier:        'starter',
    description: 'Create invoices from approved bids, track payment status, and accept Stripe card payments via public pay links.',
    route:       '/billing',
  },

  // ── SHOP ($349/mo) ─────────────────────────────────────────
  {
    id:          'time_cards',
    name:        'Employee Clock-In App',
    category:    'shop',
    icon:        '⏱️',
    core:        false,
    tier:        'shop',
    description: 'Offline-first GPS + photo clock-in tied to a specific job, operation, and work center. Punches sync when connectivity returns.',
    route:       '/time-cards',
  },
  {
    id:          'configurable_stages',
    name:        'Configurable Fab Stages',
    category:    'shop',
    icon:        '🔧',
    core:        false,
    tier:        'shop',
    description: 'Add, rename, and reorder fab stages per work center. Starter is locked to the default 8-stage set.',
  },
  {
    id:          'photo_punch',
    name:        'Photo Punch Verification',
    category:    'shop',
    icon:        '📸',
    core:        false,
    tier:        'shop',
    description: 'Require employees to capture a selfie photo at clock-in. Stored with the time-card record for verification.',
  },
  {
    id:          'job_costing',
    name:        'Job Costing Dashboard',
    category:    'shop',
    icon:        '💰',
    core:        false,
    tier:        'shop',
    description: 'Budget vs. actual: compare bid value to labor cost (time cards × bill rate) and material cost per job. Alerts when labor exceeds 80% of budget.',
  },

  // ── PRO ($649/mo) ──────────────────────────────────────────
  {
    id:          'heat_traceability',
    name:        'Material & Heat-Number Traceability',
    category:    'pro',
    icon:        '🔬',
    core:        false,
    tier:        'pro',
    comingSoon:  false,
    description: 'Record heat numbers, upload mill certs, and link them to specific piece marks. Generate a full traceability report per job.',
  },
  {
    id:          'qb_sync',
    name:        'QuickBooks Online Sync',
    category:    'pro',
    icon:        '📒',
    core:        false,
    tier:        'pro',
    comingSoon:  false,
    description: 'Bi-directional sync of invoices and customers to QuickBooks Online via OAuth. No manual re-entry.',
  },
  {
    id:          'sage_sync',
    name:        'Sage 50 CSV Export',
    category:    'pro',
    icon:        '📑',
    core:        false,
    tier:        'pro',
    comingSoon:  false,
    description: 'Export AR invoices and GL journal entries as Sage 50-formatted CSVs — no Sage API credentials required.',
  },
  {
    id:          'report_builder',
    name:        'Custom Report Builder',
    category:    'pro',
    icon:        '📈',
    core:        false,
    tier:        'pro',
    comingSoon:  false,
    description: 'Drag-and-drop column selector over Jobs, Time Cards, Bids, Invoices, and Materials. Export to CSV or PDF — no Excel required.',
  },

  // ── ENTERPRISE ($1099/mo) ──────────────────────────────────
  {
    id:          'bom_import',
    name:        'Detailing Software BOM Import',
    category:    'enterprise',
    icon:        '📥',
    core:        false,
    tier:        'enterprise',
    comingSoon:  true,
    description: 'Import piece-mark lists directly from SDS/2 or Tekla (CSV/XML). Eliminates double-entry between detailing and shop tracking.',
  },
  {
    id:          'capacity_scheduling',
    name:        'Capacity Scheduling',
    category:    'enterprise',
    icon:        '📅',
    core:        false,
    tier:        'enterprise',
    comingSoon:  true,
    description: 'Gantt-style weekly view per work center. Drag piece-mark batches onto lanes. System warns when capacity is exceeded.',
  },
  {
    id:          'api_access',
    name:        'Full REST API + Webhooks',
    category:    'enterprise',
    icon:        '🔌',
    core:        false,
    tier:        'enterprise',
    comingSoon:  true,
    description: 'Read/write jobs, piece marks, time cards, and invoices via API key. Subscribe to events (job.status_changed, bid.approved, invoice.paid) via webhooks.',
  },
  {
    id:          'multi_location',
    name:        'Multi-Location Support',
    category:    'enterprise',
    icon:        '🏭',
    core:        false,
    tier:        'enterprise',
    comingSoon:  true,
    description: 'Run unlimited shop locations from one account. Filter all screens by location. Lower tiers are single-location.',
  },
];

export const PLUGIN_CATEGORIES = {
  core:       { label: 'Core Features',         description: 'Included in every SteelOps plan — the foundation of the platform.' },
  shop:       { label: 'Shop Tier ($349/mo)',    description: 'Employee clock-in, configurable fab stages, and job costing.' },
  pro:        { label: 'Pro Tier ($649/mo)',     description: 'Heat traceability, QuickBooks sync, and custom report builder.' },
  enterprise: { label: 'Enterprise ($1099/mo)', description: 'BOM import, capacity scheduling, API access, and multi-location support.' },
};

export const pluginsByCategory = () =>
  Object.keys(PLUGIN_CATEGORIES).reduce((acc, cat) => {
    acc[cat] = PLUGINS.filter(p => p.category === cat);
    return acc;
  }, {});
